import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaCalendarAlt, FaMapMarkerAlt, FaArrowRight } from 'react-icons/fa';
import { FiSearch, FiCalendar, FiChevronDown, FiChevronUp, FiGrid } from 'react-icons/fi';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getPosters, getRepresentations } from '../api/publicApi';
import useFetch from '../hooks/useFetch';
import { SkeletonCountryBtn, SkeletonEventCard } from '../components/Skeleton';
import { IoCalendarClearOutline } from "react-icons/io5";
import EU from '../assets/allCountries.png';

const TYPE_FILTERS = [
  { value: '', label: 'TOUS' },
  { value: 'campagnes', label: 'Campagnes' },
  { value: 'annonces', label: 'Annonces' },
  { value: 'evenements', label: 'Événements' },
];

const TABS = [
  { value: '', label: 'TOUS' },
  { value: 'prevu', label: 'À VENIR' },
  { value: 'en_cours', label: 'EN COURS' },
  { value: 'termine', label: 'TERMINÉS' },
];

const STATUT_LABELS = { prevu: 'À VENIR', en_cours: 'EN COURS', termine: 'TERMINÉ' };

export default function EvenementsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);
  const [coordOpen, setCoordOpen] = useState(false);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(val), 400);
  };
  const [typeFilter, setTypeFilter] = useState('');
  const [statut, setStatut] = useState('');
  const [showGrid, setShowGrid] = useState(false);

  const posterParams = {
    limit: 100,
    ...(selectedCountry && { pays_concernes: selectedCountry.pays }),
    ...(statut && { statut }),
    ...(typeFilter && { type_affiche: typeFilter }),
  };

  const { data: postersData, loading: postersLoading, error: postersError } = useFetch(
    getPosters, posterParams, [selectedCountry?.id, statut, typeFilter]
  );
  const { data: repsData, loading: repsLoading } = useFetch(
    getRepresentations,
    { limit: 100, statut: 'active', ...(debouncedSearch && { pays: debouncedSearch }) },
    [debouncedSearch]
  );

  const posters = postersData?.data || [];
  const filteredReps = repsData?.data || [];
  const stateCountryId = location.state?.selectedCountryId;

  useEffect(() => {
    if (!filteredReps.length || !stateCountryId) return;
    const matched = filteredReps.find(r => String(r.id) === String(stateCountryId));
    if (matched) {
      setSelectedCountry(matched);
    }
    navigate('/evenements', { replace: true, state: null });
  }, [filteredReps, stateCountryId, navigate]);

  const displayedPosters = posters;

  return (
    <div className="two-col">
      {/* Mobile layout: stacked */}
      <div className="rep-mobile-top" style={{ background: '#faf9f7' }}>
        <div className="search-bar">
          <FiSearch />
          <input placeholder="RECHERCHER" value={search} onChange={e => handleSearch(e.target.value)} />
        </div>
        <div className="country-row hide-scrollbar" style={{ background: '#faf9f7' }}>
          <button
            className={`country-btn${!selectedCountry ? ' selected' : ''}`}
            onClick={() => { setSelectedCountry(null); setCoordOpen(false); }}
          >
            <img src={EU} alt="EU" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 2 }} />
            <span>Tous les pays</span>
          </button>
          {repsLoading
            ? Array.from({ length: 4 }).map((_, i) => <SkeletonCountryBtn key={i} />)
            : filteredReps.map(r => (
              <button
                key={r.id}
                className={`country-btn bg-white${selectedCountry?.id === r.id ? ' selected' : ''}${r.is_headquarter ? ' headquarters' : ''}`}
                onClick={() => { setSelectedCountry(r); setCoordOpen(false); }}
                aria-label={`${r.pays}${r.is_headquarter ? ', siège' : ''}`}
              >
                {r.drapeau_url
                  ? <img src={r.drapeau_url} alt={r.pays} style={{ width: 32, height: 'auto', borderRadius: 2 }} />
                  : <FaMapMarkerAlt size={18} />
                }
                <span>{r.pays}</span>
                {r.is_headquarter && <span className="headquarters-badge">SIÈGE</span>}
              </button>
            ))
          }
        </div>
        {selectedCountry && (
          <div className="mobile-coordonnees">
            <button className="mobile-coordonnees-toggle" onClick={() => setCoordOpen(o => !o)}>
              <span>COORDONNÉES</span>
              <span style={{ fontSize: 10 }}>{coordOpen ? '▲' : '▼'}</span>
            </button>
            {coordOpen && (
              <div className="mobile-coordonnees-body">
                <div className="mobile-coord-grid">
                  <div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Numéros Officiels</div>
                    <div style={{ fontWeight: 700, color: '#222' }}>
                      {selectedCountry.numeros_representation?.[0] || selectedCountry.telephone_representant_1 || '—'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Nom Représentant</div>
                    <div style={{ fontWeight: 700, color: '#222' }}>{selectedCountry.nom_representant}</div>
                  </div>
                  {selectedCountry.secretaire_1_nom && (
                    <div>
                      <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Secrétariat</div>
                      <div style={{ fontWeight: 700, color: '#222' }}>{selectedCountry.secretaire_1_nom}</div>
                    </div>
                  )}
                  {(selectedCountry.prayer_centers || [])
                    .filter(pc => pc.telephone_1)
                    .map(pc => (
                      <div key={pc.id}>
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>
                          {pc.type_lieu === 'espace_faustine' ? 'Espace Ste Faustine'
                            : pc.type_lieu === 'atmosphere_victoire' ? 'Atmosphère de Victoire'
                              : pc.nom}
                        </div>
                        <div style={{ fontWeight: 700, color: '#222' }}>{pc.telephone_1}</div>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Desktop sidebar */}
      <aside className="sidebar-col ">
        <div className="search-bar">
          <FiSearch />
          <input placeholder="RECHERCHER" value={search} onChange={e => handleSearch(e.target.value)} />
        </div>
        <div className="country-grid">
          <button
            className={`country-btn${!selectedCountry ? ' selected' : ''}`}
            onClick={() => setSelectedCountry(null)}>
            <FiSearch size={18} />
            <span>Tous les pays</span>
          </button>
          {repsLoading
            ? Array.from({ length: 4 }).map((_, i) => <SkeletonCountryBtn key={i} />)
            : filteredReps.map(r => (
              <button
                key={r.id}
                className={`country-btn${selectedCountry?.id === r.id ? ' selected' : ''}${r.is_headquarter ? ' headquarters' : ''}`}
                onClick={() => setSelectedCountry(r)}
                aria-label={`${r.pays}${r.is_headquarter ? ', siège' : ''}`}
              >
                {r.drapeau_url
                  ? <img src={r.drapeau_url} alt={r.pays} style={{ width: 32, height: 'auto', borderRadius: 2 }} />
                  : <FaMapMarkerAlt size={18} />
                }
                <span>{r.pays}</span>
                {r.is_headquarter && <span className="headquarters-badge">SIÈGE</span>}
              </button>
            ))
          }
        </div>
      </aside>

      {selectedCountry && (
        <div className="event-info-panel">
          <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Numéros Officiels</div>
          <div style={{ fontWeight: 700, color: '#222', marginBottom: 12 }}>
            {selectedCountry.numeros_representation?.[0] || selectedCountry.telephone_representant_1 || '—'}
          </div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Nom Représentant</div>
          <div style={{ fontWeight: 700, color: '#222', marginBottom: 12 }}>{selectedCountry.nom_representant}</div>
          {selectedCountry.secretaire_1_nom && (
            <>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Secrétariat</div>
              <div style={{ fontWeight: 700, color: '#222', marginBottom: 12 }}>{selectedCountry.secretaire_1_nom}</div>
            </>
          )}
          {(selectedCountry.prayer_centers || [])
            .filter(pc => pc.telephone_1)
            .map(pc => (
              <div key={pc.id}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>
                  {pc.type_lieu === 'espace_faustine' ? 'Espace Ste Faustine'
                    : pc.type_lieu === 'atmosphere_victoire' ? 'Atmosphère de Victoire'
                      : pc.nom}
                </div>
                <div style={{ fontWeight: 700, color: '#222', marginBottom: 12 }}>{pc.telephone_1}</div>
              </div>
            ))
          }
        </div>
      )}

      <div className="main-col px-3 pt-3">
        <div>
          <div className='bg-white p-2 pb-0 mb-md-auto mb-2'>
            <div className="event-filters">
              <span style={{ fontSize: 12, fontWeight: 600 }}>Filtrer:</span>
              {TYPE_FILTERS.map(f => (
                <button
                  key={f.value}
                  className={`filter-chip${typeFilter === f.value ? ' active' : ''}`}
                  onClick={() => setTypeFilter(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="event-tabs">
              {TABS.map(t => (
                <button
                  key={t.value}
                  className={`text-nowrap event-tab${statut === t.value ? ' active' : ''}`}
                  onClick={() => setStatut(t.value)}
                >
                  {t.label}
                </button>
              ))}
            </div>

          </div>
          {postersLoading ? (
            <div className="row">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="col-12 col-md-4"><SkeletonEventCard /></div>
              ))}
            </div>
          ) : postersError ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
              <FiCalendar size={36} style={{ display: 'block', margin: '0 auto 12px' }} />
              <p>Impossible de charger les événements.</p>
            </div>
          ) : displayedPosters.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
              <FiCalendar size={36} style={{ display: 'block', margin: '0 auto 12px' }} />
              <p>Aucun événement trouvé.</p>
            </div>
          ) : (
            <div className="row p-2">
              {displayedPosters.map((ev, idx) => (
                <div key={ev.id} className={`col-md-3 ${showGrid ? 'col-6' : 'col-12'}`}>
                  <div className="event-card shadow-sm">
                    <div className="event-card-img">
                      <img src={ev.image_url} alt={ev.titre_evenement} />
                      <span className={`event-badge rounded-pill ${ev.statut === 'termine' ? 'badge-termine' : 'badge-avenir'}`}>
                        {STATUT_LABELS[ev.statut] || ev.statut}
                      </span>
                    </div>
                    <div className="event-card-body">
                      <div className="event-date">
                        <IoCalendarClearOutline size={15} />
                        {new Date(ev.date_evenement).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase()}
                      </div>
                      <div className="event-title">{ev.titre_evenement}</div>
                      {ev.theme && (
                        <div className="event-tags fw-semibold">
                          {ev.theme.split(',').map((tag, tagIdx) => {
                            const trimmedTag = tag.trim();
                            const shouldTruncate = trimmedTag.length > 30;
                            const displayText = shouldTruncate
                              ? `${trimmedTag.substring(0, 30)}...`
                              : trimmedTag;

                            return shouldTruncate ? (
                              <OverlayTrigger
                                key={tagIdx}
                                placement="top"
                                overlay={
                                  <Tooltip id={`tooltip-${ev.id}-${tagIdx}`}>
                                    {trimmedTag}
                                  </Tooltip>
                                }
                              >
                                <span className="event-tag">{displayText}</span>
                              </OverlayTrigger>
                            ) : (
                              <span key={tagIdx} className="event-tag">{displayText}</span>
                            );
                          })}
                        </div>
                      )}
                      <div className="event-footer">
                        {ev.pays_concernes ? (
                          <span className="event-location fw-semibold">
                            <FaMapMarkerAlt color={'#8B1512'} size={15} /> {ev.pays_concernes}
                          </span>
                        ) : (
                          <div />
                        )}
                        <button
                          className={`event-link px-2 py-1 ${idx === 0 ? 'primary-btn' : 'secondary-btn'}`}
                          onClick={() => navigate(`/evenements/${ev.id}`)}
                        >
                          <span>En savoir plus</span> <FaArrowRight size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Mobile floating grid toggle */}
        <button
          className="semence-fab mobile-only"
          onClick={() => setShowGrid(g => !g)}
          aria-label="Toggle grid"
        >
          <FiGrid size={20} />
        </button>
      </div>
    </div>
  );
}
